import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

interface EmailData {
  to: string;
  subject: string;
  content: string;
  from?: string;
}

interface EmailResponse {
  success: boolean;
  message?: string;
  emailId?: string;
  error?: string;
}

class EmailService {
  private sendEmailFunction = httpsCallable(functions, 'sendEmail');
  private getEmailHistoryFunction = httpsCallable(functions, 'getEmailHistory');

  /**
   * Envoie un email via Firebase Functions
   */
  async sendEmail(emailData: EmailData): Promise<EmailResponse> {
    try {
      const result = await this.sendEmailFunction(emailData);
      return result.data as EmailResponse;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      throw new Error('Impossible d\'envoyer l\'email. Veuillez réessayer.');
    }
  }

  /**
   * Récupère l'historique des emails envoyés
   */
  async getEmailHistory(limit: number = 50): Promise<any[]> {
    try {
      const result = await this.getEmailHistoryFunction({ limit });
      const response = result.data as { success: boolean; emails: any[] };
      return response.emails || [];
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return [];
    }
  }

  /**
   * Formate un email pour une demande de site web
   */
  formatWebsiteRequestEmail(formData: any, restaurantData?: any, userData?: any): EmailData {
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
        <div style="background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          <h1 style="color: #2563eb; text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 15px;">
            🌐 NOUVELLE DEMANDE DE SITE WEB - EspaceResto
          </h1>
          
          <div style="margin-bottom: 25px;">
            <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
              👤 INFORMATIONS CLIENT
            </h2>
            <ul style="list-style: none; padding: 0; margin: 0;">
               <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Nom du propriétaire:</strong> ${userData?.displayName || 'Non spécifié'}</li>
               <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Email du propriétaire:</strong> ${userData?.email || formData.restaurantEmail}</li>
               <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Rôle:</strong> ${userData?.role || 'Non spécifié'}</li>
               <li style="padding: 5px 0;"><strong>Date de demande:</strong> ${formData.timestamp}</li>
             </ul>
          </div>
          
          <div style="margin-bottom: 25px;">
            <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
              🏪 INFORMATIONS ÉTABLISSEMENT
            </h2>
            <ul style="list-style: none; padding: 0; margin: 0;">
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Nom du restaurant:</strong> ${restaurantData?.name || formData.restaurantName}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Email du restaurant:</strong> ${restaurantData?.email || formData.restaurantEmail}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Téléphone:</strong> ${restaurantData?.phone || 'Non spécifié'}</li>
              <li style="padding: 5px 0;"><strong>Adresse:</strong> ${restaurantData?.address || 'Non spécifiée'}</li>
            </ul>
          </div>
          
          <div style="margin-bottom: 25px;">
            <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
              🌐 DEMANDE DE SITE WEB
            </h2>
            <ul style="list-style: none; padding: 0; margin: 0;">
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Site web existant:</strong> ${formData.hasExistingWebsite}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Conserver le domaine actuel:</strong> ${formData.keepCurrentDomain}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Domaine actuel:</strong> ${formData.currentDomain || 'Aucun'}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Aide pour le domaine:</strong> ${formData.needDomainHelp}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Inspiration design:</strong> ${formData.designInspiration || 'Aucune'}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Besoins spécifiques:</strong> ${formData.specificNeeds || 'Aucun'}</li>
              <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Liens réseaux sociaux:</strong> ${formData.socialLinks || 'Aucun'}</li>
              <li style="padding: 5px 0;"><strong>Demandes supplémentaires:</strong> ${formData.additionalRequests || 'Aucune'}</li>
            </ul>
          </div>
          
          <div style="margin-bottom: 25px;">
            <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
              🔧 DONNÉES TECHNIQUES
            </h2>
            <ul style="list-style: none; padding: 0; margin: 0;">
               <li style="padding: 5px 0; border-bottom: 1px solid #e5e7eb;"><strong>Template menu:</strong> ${restaurantData?.menuTemplate || 'Non spécifié'}</li>
               <li style="padding: 5px 0;"><strong>Créé le:</strong> ${restaurantData?.createdAt ? new Date(restaurantData.createdAt.seconds * 1000).toLocaleDateString('fr-FR') : 'Non spécifié'}</li>
             </ul>
          </div>
          
          
        </div>
      </div>
    `;

    return {
      to: 'contact@espaceresto.fr',
      subject: `🌐 Nouvelle demande de site web - ${restaurantData?.name || formData.restaurantName}`,
      content,
      from: 'contact@espaceresto.fr'
    };
  }
}

export const emailService = new EmailService();
export type { EmailData, EmailResponse };